import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { API, logErr, inr, fmtErr } from "../../lib/api";

const STATUSES = ["requested", "quoted", "confirmed", "in_production", "shipped", "delivered", "cancelled"];

export default function AdminCustomOrders() {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => API.get("/admin/custom-orders", { params: filter ? { status: filter } : {} })
    .then((r) => setOrders(r.data)).catch((err) => logErr("Failed to load custom orders:", err)), [filter, API, logErr]);
  useEffect(() => { load(); document.title = "Custom Orders | Admin"; }, [load]);

  const setStatus = async (id, status) => {
    try {
      await API.put(`/admin/custom-orders/${id}/status`, { status });
      toast.success(`Status → ${status.replace(/_/g, " ")}`);
      load();
      setDetail((d) => (d && d.id === id ? { ...d, status } : d));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!orders) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div data-testid="admin-custom-orders">
      <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Custom Orders <span className="text-muted-foreground text-lg font-normal">({orders.length})</span></h1>
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button onClick={() => setFilter("")} data-testid="custom-filter-all"
          className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border shrink-0 ${!filter ? "border-gold text-gold" : "border-[#262626] text-muted-foreground"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`custom-filter-${s}`}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border shrink-0 transition-colors ${filter === s ? "border-gold text-gold" : "border-[#262626] text-muted-foreground hover:border-gold/50"}`}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="border border-[#262626] bg-ink-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-[#262626]">
              <th className="p-4">Ref</th><th className="p-4">Piece</th><th className="p-4">Print</th>
              <th className="p-4">Customer</th><th className="p-4">Total</th><th className="p-4">Status</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-[#262626] hover:bg-ink-elevated/50 cursor-pointer" onClick={() => setDetail(o)} data-testid={`custom-row-${o.ref}`}>
                <td className="p-4 font-bold text-xs">{o.ref}</td>
                <td className="p-4 text-xs">{o.garment_label}<br /><span className="text-muted-foreground">{o.size}/{o.color} × {o.qty}</span></td>
                <td className="p-4 text-xs">{o.text ? <span className="text-gold">"{o.text}"</span> : o.design_name ? <span className="text-gold">{o.design_name}</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="p-4 text-xs">{o.name}<br /><span className="text-muted-foreground">{o.email}</span></td>
                <td className="p-4 text-xs font-bold text-gold">{inr(o.total)}</td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${o.status === "delivered" ? "bg-green-400/10 text-green-400" : o.status === "cancelled" ? "bg-red-400/10 text-red-400" : "bg-gold/10 text-gold"}`}>
                    {o.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="p-4 text-xs text-gold">View →</td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-xs text-muted-foreground">No custom orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid="custom-detail-modal">
          <div className="bg-ink-surface border border-[#262626] w-full max-w-xl p-6 my-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display font-bold text-xl">{detail.ref}</h2>
                <p className="text-xs text-muted-foreground">{detail.created_at.slice(0, 16).replace("T", " ")}</p>
              </div>
              <button onClick={() => setDetail(null)} data-testid="close-custom-detail"><X size={18} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 text-sm mb-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-2">Piece</p>
                <p className="font-bold">{detail.garment_label}</p>
                <p className="text-xs text-muted-foreground">{detail.gsm ? `${detail.gsm} GSM · ` : ""}{detail.size} / {detail.color} · Qty {detail.qty}</p>
                {detail.collection_label && <p className="text-[10px] text-gold mt-1 uppercase tracking-widest font-bold">{detail.collection_label} collection</p>}
                {detail.text && <p className="text-xs mt-2">Print: <span className="text-gold font-bold">"{detail.text}"</span> ({detail.text_color}, {detail.placement})</p>}
                {detail.design_name && <p className="text-xs mt-2">Design: <span className="text-gold font-bold">{detail.design_name}</span></p>}
                {detail.design_url && <img src={detail.design_url} alt="Selected design" className="h-16 w-16 object-contain mt-2 border border-[#262626] bg-ink p-1" />}
                {detail.notes && <p className="text-xs text-muted-foreground mt-2">Notes: {detail.notes}</p>}
                <p className="font-display font-bold text-gold mt-3">{inr(detail.unit_price)} × {detail.qty} = {inr(detail.total)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-2">Customer</p>
                <p className="font-bold">{detail.name}</p>
                <p className="text-xs text-muted-foreground">{detail.email}<br />{detail.phone}</p>
                {detail.user_id && <p className="text-[10px] text-gold mt-1">Registered customer</p>}
              </div>
            </div>
            <select value={detail.status} onChange={(e) => setStatus(detail.id, e.target.value)} data-testid="custom-status-select"
              className="bg-ink border border-[#262626] px-3 py-2.5 text-xs uppercase tracking-wider outline-none focus:border-gold w-full">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
            <div className="mt-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-2">Timeline</p>
              {detail.timeline.map((t) => (
                <p key={`${t.status}-${t.at}`} className="text-xs text-muted-foreground">
                  <span className="text-foreground font-bold capitalize">{t.status.replace(/_/g, " ")}</span> — {t.at.slice(0, 16).replace("T", " ")}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
